import * as React from 'react';
import { useNavigate } from 'react-router';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import { IMockupGenerationLog, IMockupGenerationLogsQueryParams, asBool } from '../../api/mockupGenerationLogs';
import { fmtBytes, fmtDateTime, fmtRatio, fmtSec, fmtSize } from '../../lib/mockupGenerationLogs/format';
import { LOST_SUBJECT_THRESHOLD } from '../../lib/mockupGenerationLogs/aggregate';
import { useStyles } from './styles';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: theme.palette.action.selected,
    },
}));

const STATUS_COLORS: Record<string, string> = {
    ok: '#0ca30c',
    error: '#d03b3b',
};

const ERROR_KIND_COLORS: Record<string, string> = {
    silhouette: '#fab219',
    background_removal: '#ec835a',
    validation: '#898781',
    unavailable: '#4a3aa7',
    internal: '#d03b3b',
};

interface Props {
    logs: IMockupGenerationLog[];
    paging: IMockupGenerationLogsQueryParams;
    pagingUpdate: (params: IMockupGenerationLogsQueryParams) => void;
}

const qualityColor = (q: number | null) => {
    if (q === null || q === undefined) return undefined;
    if (q >= 0.8) return '#0ca30c';
    if (q >= 0.6) return '#fab219';
    return '#d03b3b';
};

export default function MockupGenerationLogsTable({ logs, paging, pagingUpdate }: Props) {
    const classes = useStyles();
    const navigator = useNavigate();

    const handleChangePage = (_: unknown, newPage: number) => {
        pagingUpdate({ ...paging, page: newPage + 1 });
    };

    const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
        pagingUpdate({ ...paging, page: 1, limit: +e.target.value });
    };

    const openDetails = (log: IMockupGenerationLog) => navigator(`/mockup-generation-logs/${log.eventId}`);

    const flags = (log: IMockupGenerationLog) => {
        const out: string[] = [];
        if (asBool(log.glbOnly)) out.push('glbOnly');
        if (asBool(log.noBgProvided)) out.push('noBg');
        if (asBool(log.fastMode)) out.push('fast');
        if (asBool(log.balancedMode)) out.push('balanced');
        if (asBool(log.rbRetryPerformed)) out.push('retry');
        if (asBool(log.includePreviews)) out.push(`previews:${log.previewCount ?? 0}`);
        if ((log.overridesCount ?? 0) > 0) out.push(`overrides:${log.overridesCount}`);
        return out;
    };

    return (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: '65vh', position: 'relative' }}>
                <Table stickyHeader size="small" aria-label="mockup generation logs table">
                    <TableHead>
                        <TableRow>
                            <TableCell align="center" style={{ minWidth: 150 }}>Time (local)</TableCell>
                            <TableCell align="center" style={{ minWidth: 110 }}>Event ID</TableCell>
                            <TableCell align="center" style={{ minWidth: 90 }}>Status</TableCell>
                            <TableCell align="center" style={{ minWidth: 160 }}>Error</TableCell>
                            <TableCell align="center" style={{ minWidth: 90 }}>Customer</TableCell>
                            <TableCell align="center" style={{ minWidth: 90 }}>Template</TableCell>
                            <TableCell align="center" style={{ minWidth: 80 }}>Total</TableCell>
                            <TableCell align="center" style={{ minWidth: 80 }}>RemoveBG</TableCell>
                            <TableCell align="center" style={{ minWidth: 80 }}>3D</TableCell>
                            <TableCell align="center" style={{ minWidth: 90 }}>Source</TableCell>
                            <TableCell align="center" style={{ minWidth: 80 }}>RB quality</TableCell>
                            <TableCell align="center" style={{ minWidth: 80 }}>Silhouette</TableCell>
                            <TableCell align="center" style={{ minWidth: 80 }}>GLB</TableCell>
                            <TableCell align="center" style={{ minWidth: 160 }}>Flags</TableCell>
                            <TableCell align="center" style={{ minWidth: 120 }}>Server</TableCell>
                            <TableCell align="center" style={{ minWidth: 90 }}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {logs.map((log) => {
                            const p3d = [log.p3dMeshSec, log.p3dGlbSec, log.p3dPreviewsSec]
                                .filter((v): v is number => v !== null && v !== undefined)
                                .reduce((a, b) => a + b, 0);
                            const lostSubject = (log.p3dSilIgnoredShare ?? 0) > LOST_SUBJECT_THRESHOLD;
                            return (
                                <StyledTableRow key={log.id ?? log.eventId} tabIndex={-1} onClick={() => openDetails(log)}>
                                    <TableCell align="center">{fmtDateTime(log.ts)}</TableCell>
                                    <TableCell align="center">
                                        <span style={{ fontWeight: 600 }}>{log.eventId}</span>
                                        {log.tempEventId ? (
                                            <div className={classes.mono} style={{ opacity: 0.6 }}>tmp {log.tempEventId}</div>
                                        ) : null}
                                    </TableCell>
                                    <TableCell align="center">
                                        <div className={classes.statusBadge} style={{ backgroundColor: STATUS_COLORS[log.status] || '#898781' }}>
                                            {log.status?.toUpperCase()} {log.httpStatus ? `· ${log.httpStatus}` : ''}
                                        </div>
                                    </TableCell>
                                    <TableCell align="center">
                                        {log.errorKind ? (
                                            <>
                                                <div
                                                    className={classes.statusBadge}
                                                    style={{ backgroundColor: ERROR_KIND_COLORS[log.errorKind] || '#898781', marginBottom: 4 }}
                                                >
                                                    {log.errorKind}
                                                </div>
                                                <Tooltip title={log.errorMessage || ''}>
                                                    <span className={classes.errorText}>{log.errorMessage || '—'}</span>
                                                </Tooltip>
                                            </>
                                        ) : '—'}
                                    </TableCell>
                                    <TableCell align="center">{log.customerId ?? '—'}</TableCell>
                                    <TableCell align="center">{log.template ?? '—'}</TableCell>
                                    <TableCell align="center" style={{ fontWeight: 600 }}>{fmtSec(log.totalSec)}</TableCell>
                                    <TableCell align="center">{fmtSec(log.rbSec)}</TableCell>
                                    <TableCell align="center">{log.status === 'ok' ? fmtSec(p3d) : '—'}</TableCell>
                                    <TableCell align="center">
                                        {fmtSize(log.sourceW, log.sourceH)}
                                        {log.sourceMp ? <div style={{ fontSize: 11, opacity: 0.7 }}>{log.sourceMp} MP</div> : null}
                                    </TableCell>
                                    <TableCell align="center" style={{ fontWeight: 600, color: qualityColor(log.rbOverallQuality) }}>
                                        {fmtRatio(log.rbOverallQuality, 2)}
                                    </TableCell>
                                    <TableCell align="center">
                                        {log.p3dSilComponents ?? '—'}
                                        {lostSubject ? (
                                            <Tooltip title={`ignoredShare ${fmtRatio(log.p3dSilIgnoredShare, 2)} > ${LOST_SUBJECT_THRESHOLD} — part of the subject may be lost`}>
                                                <span style={{ color: '#d03b3b', marginLeft: 4 }}>⚠</span>
                                            </Tooltip>
                                        ) : null}
                                    </TableCell>
                                    <TableCell align="center">
                                        {fmtBytes(log.p3dGlbBytes)}
                                        {log.p3dTriangles ? <div style={{ fontSize: 11, opacity: 0.7 }}>{log.p3dTriangles.toLocaleString()} tri</div> : null}
                                    </TableCell>
                                    <TableCell align="center">
                                        {flags(log).map((f) => (
                                            <span key={f} className={classes.flag}>{f}</span>
                                        ))}
                                    </TableCell>
                                    <TableCell align="center">
                                        <div className={classes.mono}>{log.serverHost ?? '—'}</div>
                                        <div className={classes.mono} style={{ opacity: 0.6 }}>v{log.serverVersion ?? '?'} · {log.rbDevice ?? '—'}</div>
                                    </TableCell>
                                    <TableCell align="center">
                                        <div
                                            className={classes.detailsButton}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openDetails(log);
                                            }}
                                        >
                                            Details
                                        </div>
                                    </TableCell>
                                </StyledTableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[25, 50, 100, 500]}
                component="div"
                count={paging.total || 0}
                rowsPerPage={paging.limit || 50}
                page={(paging.page || 1) - 1}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Paper>
    );
}
